import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

/**
 * Interface for defining export columns with transformation support
 */
export interface ExportColumn<T> {
  key: string;                    // Property path (e.g., 'name' or 'kunde.name')
  label: string;                  // Display name for header
  transform?: (value: any, row: T) => string; // Optional transformation function
  include?: boolean;              // Whether to include in export (default: true)
}

/**
 * Options for table export
 */
export interface ExportOptions<T> {
  data: T[];
  columns: ExportColumn<T>[];
  filename?: string;
  format: 'csv' | 'xlsx';
  includeHeaders?: boolean;        // Include column headers (default: true)
  dateFormat?: string;            // Date format string (default: 'dd.MM.yyyy')
}

@Injectable({
  providedIn: 'root'
})
export class TableExportService {

  /**
   * Export table data to CSV or Excel format
   */
  async exportTable<T>(options: ExportOptions<T>): Promise<void> {
    if (options.format === 'csv') {
      this.exportToCSV(options);
    } else if (options.format === 'xlsx') {
      await this.exportToExcel(options);
    } else {
      throw new Error(`Unsupported export format: ${options.format}`);
    }
  }

  /**
   * Export data to CSV format
   */
  exportToCSV<T>(options: ExportOptions<T>): void {
    try {
      const { data, columns, filename, includeHeaders = true } = options;
      
      // Filter columns that should be included
      const activeColumns = columns.filter(col => col.include !== false);
      
      // Build CSV content
      const rows: string[] = [];
      
      // Add headers if requested
      if (includeHeaders) {
        const headers = activeColumns.map(col => this.escapeCSVValue(col.label));
        rows.push(headers.join(','));
      }
      
      // Add data rows
      data.forEach(row => {
        const values = activeColumns.map(col => {
          const value = this.getNestedValue(row, col.key);
          const transformed = col.transform ? col.transform(value, row) : this.formatValue(value);
          return this.escapeCSVValue(transformed);
        });
        rows.push(values.join(','));
      });
      
      // Create CSV content
      const csvContent = rows.join('\n');
      
      // Add BOM for UTF-8 (Excel compatibility)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const exportFilename = filename ? `${filename}-${timestamp}.csv` : `export-${timestamp}.csv`;
      
      // Download file
      this.downloadFile(blob, exportFilename);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      throw error;
    }
  }

  /**
   * Export data to Excel format (XLSX)
   * Uses xlsx library to generate proper .xlsx files
   */
  async exportToExcel<T>(options: ExportOptions<T>): Promise<void> {
    try {
      await this.exportToExcelWithLibrary(options);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw new Error('Failed to export to Excel format. Please try CSV export instead.');
    }
  }

  /**
   * Export to Excel using xlsx library
   */
  private async exportToExcelWithLibrary<T>(options: ExportOptions<T>): Promise<void> {
    const { data, columns, filename, includeHeaders = true } = options;
    
    // Filter columns that should be included
    const activeColumns = columns.filter(col => col.include !== false);
    
    // Build worksheet data
    const worksheetData: any[][] = [];
    
    // Add headers if requested
    if (includeHeaders) {
      const headers = activeColumns.map(col => col.label);
      worksheetData.push(headers);
    }
    
    // Add data rows
    data.forEach(row => {
      const values = activeColumns.map(col => {
        const value = this.getNestedValue(row, col.key);
        return col.transform ? col.transform(value, row) : this.formatValue(value);
      });
      worksheetData.push(values);
    });
    
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths for better readability
    const maxWidths = activeColumns.map((col, colIndex) => {
      let maxLength = col.label.length;
      data.forEach(row => {
        const value = this.getNestedValue(row, col.key);
        const transformed = col.transform ? col.transform(value, row) : this.formatValue(value);
        maxLength = Math.max(maxLength, String(transformed).length);
      });
      return { wch: Math.min(maxLength + 2, 50) }; // Max width of 50, min of label length + 2
    });
    worksheet['!cols'] = maxWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    
    // Generate filename with .xlsx extension
    const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const exportFilename = filename ? `${filename}-${timestamp}.xlsx` : `export-${timestamp}.xlsx`;
    
    // Write file
    XLSX.writeFile(workbook, exportFilename);
  }

  /**
   * Get nested value from object using dot notation (e.g., 'kunde.name')
   */
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return null;
    
    const keys = path.split('.');
    let value = obj;
    
    for (const key of keys) {
      if (value === null || value === undefined) {
        return null;
      }
      value = value[key];
    }
    
    return value;
  }

  /**
   * Format value for export (handle null, undefined, dates, etc.)
   */
  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein';
    }
    
    if (value instanceof Date) {
      return this.formatDate(value);
    }
    
    // If it's a string that looks like a date, try to format it
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return this.formatDate(date);
        }
      } catch (e) {
        // Not a valid date, return as-is
      }
    }
    
    return String(value);
  }

  /**
   * Format date to German format (dd.MM.yyyy)
   */
  private formatDate(date: Date | string): string {
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Escape CSV value (handle quotes, commas, newlines)
   */
  private escapeCSVValue(value: string): string {
    if (value === null || value === undefined) {
      return '';
    }
    
    const stringValue = String(value);
    
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  }

  /**
   * Download file using blob
   */
  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

